/*
  Tabbed steps.

  Vertical tab list driving a set of panels, following the WAI-ARIA tabs
  pattern with automatic activation: arrow keys move between tabs and select
  as they go, Home/End jump to the ends, and a roving tabindex keeps exactly
  one tab in the page's tab order.

  Being a custom element means the theme editor gets re-initialisation for
  free — `shopify:section:load` re-parses the section's markup, which upgrades
  the new element and fires connectedCallback again. No page reload needed.
*/
if (!customElements.get('tabbed-steps')) {
  class TabbedSteps extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

      // A mismatch means the markup was altered; leave the no-JS state alone
      // rather than hiding panels that nothing can reveal again.
      if (this.tabs.length === 0 || this.tabs.length !== this.panels.length) return;

      this.onTabClick = this.onTabClick.bind(this);
      this.onTabKeydown = this.onTabKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
        tab.addEventListener('keydown', this.onTabKeydown);
      });

      const preselected = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.select(preselected === -1 ? 0 : preselected, { focusTab: false });

      if (window.Shopify && window.Shopify.designMode) {
        this.addEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    disconnectedCallback() {
      if (!this.tabs) return;

      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.onTabClick);
        tab.removeEventListener('keydown', this.onTabKeydown);
      });

      this.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    onTabClick(event) {
      // Pointer users keep focus where they clicked; no need to force it.
      this.select(this.tabs.indexOf(event.currentTarget), { focusTab: false });
    }

    onTabKeydown(event) {
      const step = { ArrowUp: -1, ArrowLeft: -1, ArrowDown: 1, ArrowRight: 1 }[event.key];
      const current = this.tabs.indexOf(event.currentTarget);
      let next = null;

      if (step !== undefined) {
        next = (current + step + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        next = 0;
      } else if (event.key === 'End') {
        next = this.tabs.length - 1;
      }

      if (next === null) return;

      event.preventDefault();
      this.select(next);
    }

    onBlockSelect(event) {
      const index = this.panels.findIndex(
        (panel) => panel === event.target || panel.contains(event.target)
      );

      // Reveal the block the merchant just selected, so the editor can scroll
      // to it and they can see their edits land.
      if (index !== -1) this.select(index, { focusTab: false });
    }

    select(index, { focusTab = true } = {}) {
      this.tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach((panel, i) => {
        const isActive = i === index;
        panel.classList.toggle('tabbed-steps__panel--active', isActive);
        panel.toggleAttribute('hidden', !isActive);
      });

      if (focusTab) this.tabs[index].focus();
    }
  }

  customElements.define('tabbed-steps', TabbedSteps);
}
